import { useState } from 'react';

import { getEditView } from 'modules/archive';
import { AuthorizedContent, admin } from 'modules/auth';
import { useProjectAccessStatus } from 'modules/auth';
import { getCurrentUser, submitData } from 'modules/data';
import { Form } from 'modules/forms';
import { useI18n } from 'modules/i18n';
import { usePathBase, useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { underscore } from 'modules/strings';
import { InlineNotification } from 'modules/ui';
import PropTypes from 'prop-types';
import { FaAngleDown, FaAngleUp, FaPencilAlt, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

import ContentField from '../display/ContentField';

/**
 * Displays and edits one field using localized metadata settings from project details.
 * @param {Object} props - Field display, editing, validation, and publication options.
 * @returns {React.ReactElement|null} Visible field, edit form, or loading/error state.
 */
export default function SingleValueWithForm({
    readOnly,
    elementType,
    type,
    multiLocale,
    value,
    editValue, // used to pass a different value to the form for editing, e.g. for arrays that need to be joined into a string
    validate,
    values,
    withEmpty,
    help,
    individualErrorMsg,
    optionsScope,
    obj,
    noLabel,
    linkUrls,
    collapse,
    children,
    attribute,
    noStatusCheckbox,
    extraStatusCheckboxes = [],
    hideEmpty = false,
}) {
    const [editing, setEditing] = useState(false);
    const [collapsed, setCollapsed] = useState(collapse);
    const isStringValue = typeof value === 'string';

    const { t } = useI18n();
    const pathBase = usePathBase();
    const { project, isLoading, error } = useProject({
        include: ['metadata_fields'],
    });
    const { projectAccessGranted } = useProjectAccessStatus(project);

    const user = useSelector(getCurrentUser);
    const editView = useSelector(getEditView);
    const dispatch = useDispatch();

    if (error)
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={error.message}
                isClosable={false}
            />
        );
    if (isLoading || !project) return <Spinner />;

    const metadataField = (project?.metadata_fields || []).find(
        (m) => m.name === attribute
    );

    const label =
        metadataField?.label ||
        t(`activerecord.attributes.${underscore(obj.type)}.${attribute}`);

    const formElements = [];

    const formElement = {
        elementType: elementType,
        type: type,
        multiLocale: multiLocale,
        attribute: attribute,
        value: editValue ?? value,
        label: label,
        validate: validate,
        data: obj,
        values: values,
        withEmpty: withEmpty,
        help: help,
        individualErrorMsg: individualErrorMsg,
        optionsScope: optionsScope,
    };

    const statusCheckbox = {
        elementType: 'input',
        attribute: `public_${attribute}`,
        value:
            obj.properties?.public_attributes?.[attribute]?.toString() ===
            'true',
        labelKey: 'activerecord.attributes.default.publish',
        type: 'checkbox',
        group: 'status-options',
    };

    formElements.push(formElement);
    if (!noStatusCheckbox) {
        formElements.push(statusCheckbox);
        extraStatusCheckboxes.forEach((checkbox) => {
            formElements.push({
                elementType: 'input',
                type: 'checkbox',
                group: 'status-options',
                ...checkbox,
            });
        });
    }

    const form = () => (
        <Form
            scope={underscore(obj.type)}
            onSubmit={(params) => {
                dispatch(
                    submitData({ pathBase }, params, {
                        updateStateBeforeSubmit: true,
                    })
                );
                setEditing(false);
            }}
            onCancel={() => setEditing(false)}
            formClasses="default single-value"
            className="ContentField"
            data={obj}
            elements={formElements}
        />
    );

    const show = () => {
        if (
            (admin({ user, editView, project }, obj, 'update') &&
                typeof metadataField !== 'undefined') ||
            (((projectAccessGranted && metadataField?.use_in_details_view) ||
                (!projectAccessGranted &&
                    metadataField?.display_on_landing_page)) &&
                obj.properties?.public_attributes?.[attribute]?.toString() ===
                    'true' &&
                !(hideEmpty && !value))
        ) {
            return (
                <ContentField
                    noLabel={noLabel}
                    label={label}
                    value={
                        isStringValue && collapsed
                            ? value?.substring(0, 500)
                            : value
                    }
                    linkUrls={linkUrls}
                >
                    {!readOnly && (
                        <>
                            {collapse && (
                                <button
                                    type="button"
                                    className="Button Button--transparent Button--icon"
                                    title={t(collapsed ? 'show' : 'hide')}
                                    onClick={() => setCollapsed(!collapsed)}
                                    data-testid="collapse-toggle-button"
                                >
                                    {collapsed ? (
                                        <FaAngleDown className="Icon Icon--editorial" />
                                    ) : (
                                        <FaAngleUp className="Icon Icon--editorial" />
                                    )}
                                </button>
                            )}
                            {children}
                            <AuthorizedContent object={obj} action="update">
                                <button
                                    type="button"
                                    className="Button Button--transparent Button--icon"
                                    title={t(
                                        `edit.default.${editing ? 'cancel' : 'edit'}`
                                    )}
                                    data-testid="edit-toggle-button"
                                    onClick={() => setEditing(!editing)}
                                >
                                    {editing ? (
                                        <FaTimes className="Icon Icon--editorial" />
                                    ) : (
                                        <FaPencilAlt className="Icon Icon--editorial" />
                                    )}
                                </button>
                            </AuthorizedContent>
                        </>
                    )}
                </ContentField>
            );
        } else {
            return null;
        }
    };

    return admin({ user, editView, project }, obj, 'update') && editing
        ? form()
        : show();
}

SingleValueWithForm.propTypes = {
    readOnly: PropTypes.bool,
    elementType: PropTypes.string,
    type: PropTypes.string,
    multiLocale: PropTypes.bool,
    value: PropTypes.any,
    editValue: PropTypes.any,
    validate: PropTypes.func,
    values: PropTypes.array,
    withEmpty: PropTypes.bool,
    help: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    individualErrorMsg: PropTypes.string,
    optionsScope: PropTypes.string,
    obj: PropTypes.object,
    noLabel: PropTypes.bool,
    linkUrls: PropTypes.bool,
    collapse: PropTypes.bool,
    children: PropTypes.node,
    attribute: PropTypes.string,
    noStatusCheckbox: PropTypes.bool,
    extraStatusCheckboxes: PropTypes.arrayOf(
        PropTypes.shape({
            attribute: PropTypes.string.isRequired,
            value: PropTypes.bool,
            labelKey: PropTypes.string,
        })
    ),
    hideEmpty: PropTypes.bool,
};
